
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import SearchableSelect from './components/SearchableSelect';

const WorkspaceManager = ({ token, onSelectWorkspace }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [members, setMembers] = useState([]);
    const [newWorkspaceName, setNewWorkspaceName] = useState('');
    const [newMemberUsername, setNewMemberUsername] = useState('');
    const [newMemberRole, setNewMemberRole] = useState('viewer');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [aiPolicy, setAiPolicy] = useState({
        allowed_providers: [],
        allowed_models: [],
        redaction_enabled: true,
        require_human_approval_for_high_impact: true
    });
    const [policyProvidersInput, setPolicyProvidersInput] = useState('');
    const [policyModelsInput, setPolicyModelsInput] = useState('');
    const [approvals, setApprovals] = useState([]);
    const [workspaceConcurrencyLimit, setWorkspaceConcurrencyLimit] = useState(10);
    const [errorWorkflowId, setErrorWorkflowId] = useState('');
    const [wsWorkflows, setWsWorkflows] = useState([]);
    const [ewSaving, setEwSaving] = useState(false);

    useEffect(() => {
        fetchWorkspaces();
    }, [token]);

    const fetchWorkspaces = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/workspaces/`, { params: { member_only: true } });
            setWorkspaces(res.data);
        } catch (err) {
            setError("Failed to fetch workspaces");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const createWorkspace = async () => {
        if (!newWorkspaceName) return;
        try {
            await axios.post(`${API_BASE_URL}/workspaces/`, { name: newWorkspaceName });
            setNewWorkspaceName('');
            setError(null);
            fetchWorkspaces();
        } catch (err) {
            setError("Failed to create workspace");
            console.error(err);
        }
    };

    const selectWorkspace = async (workspace) => {
        setSelectedWorkspace(workspace);
        try {
            const [workspaceRes, policyRes, workflowsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/workspaces/${workspace.id}`),
                axios.get(`${API_BASE_URL}/workspaces/${workspace.id}/ai-policy`),
                axios.get(`${API_BASE_URL}/workflows/`, { params: { workspace_id: workspace.id, limit: 200 } })
            ]);
            setSelectedWorkspace(workspaceRes.data);
            setMembers(workspaceRes.data.members);
            setAiPolicy(policyRes.data);
            setWorkspaceConcurrencyLimit(workspaceRes.data.max_concurrent_runs || 10);
            setErrorWorkflowId(workspaceRes.data.error_workflow_id || '');
            setWsWorkflows(Array.isArray(workflowsRes.data) ? workflowsRes.data : []);
            setPolicyProvidersInput((policyRes.data.allowed_providers || []).join(', '));
            setPolicyModelsInput((policyRes.data.allowed_models || []).join(', '));
            if (workspaceRes.data.my_role === 'admin') {
                fetchApprovals(workspace.id);
            } else {
                setApprovals([]);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchApprovals = async (workspaceId) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/copilot/approvals`, {
                params: { workspace_id: workspaceId, status: 'pending' }
            });
            setApprovals(res.data || []);
        } catch (err) {
            setApprovals([]);
        }
    };

    const addMember = async () => {
        if (!selectedWorkspace || !newMemberUsername) return;

        try {
            await axios.post(`${API_BASE_URL}/workspaces/${selectedWorkspace.id}/members`, {
                username: newMemberUsername,
                role: newMemberRole
            });
            setNewMemberUsername('');
            setError(null);
            selectWorkspace(selectedWorkspace); // Refresh
        } catch (err) {
            setError("Failed to add member: " + (err.response?.data?.detail || err.message));
        }
    };

    const removeMember = async (userId) => {
        if (!(await window.confirm("Are you sure?"))) return;
        try {
            await axios.delete(`${API_BASE_URL}/workspaces/${selectedWorkspace.id}/members/${userId}`);
            setError(null);
            selectWorkspace(selectedWorkspace); // Refresh
        } catch (err) {
            setError("Failed to remove member");
        }
    };

    const updateMemberRole = async (userId, newRole) => {
        try {
            await axios.put(`${API_BASE_URL}/workspaces/${selectedWorkspace.id}/members/${userId}`, {
                role: newRole
            });
            setError(null);
            selectWorkspace(selectedWorkspace);
        } catch (err) {
             setError("Failed to update role");
        }
    };

    const deleteWorkspace = async () => {
        if (!selectedWorkspace) return;
        if (!(await window.confirm(`Are you sure you want to delete workspace "${selectedWorkspace.name}"? This cannot be undone.`))) return;

        try {
            await axios.delete(`${API_BASE_URL}/workspaces/${selectedWorkspace.id}`);
            setSelectedWorkspace(null);
            setError(null);
            fetchWorkspaces();
        } catch (err) {
            setError("Failed to delete workspace: " + (err.response?.data?.detail || err.message));
        }
    };

    const saveAiPolicy = async () => {
        if (!selectedWorkspace || selectedWorkspace.my_role !== 'admin') return;
        try {
            const payload = {
                allowed_providers: policyProvidersInput
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean),
                allowed_models: policyModelsInput
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean),
                redaction_enabled: aiPolicy.redaction_enabled,
                require_human_approval_for_high_impact: aiPolicy.require_human_approval_for_high_impact
            };
            const res = await axios.put(`${API_BASE_URL}/workspaces/${selectedWorkspace.id}/ai-policy`, payload);
            setAiPolicy(res.data);
            setPolicyProvidersInput((res.data.allowed_providers || []).join(', '));
            setPolicyModelsInput((res.data.allowed_models || []).join(', '));
            setError(null);
        } catch (err) {
            setError("Failed to save AI policy");
        }
    };

    const reviewApproval = async (approvalId, decision) => {
        if (!selectedWorkspace || selectedWorkspace.my_role !== 'admin') return;
        try {
            await axios.post(`${API_BASE_URL}/copilot/approvals/${approvalId}/decision`, { decision });
            setError(null);
            fetchApprovals(selectedWorkspace.id);
        } catch (err) {
            setError("Failed to submit approval decision");
        }
    };

    const saveWorkspaceErrorWorkflow = async () => {
        if (!selectedWorkspace || selectedWorkspace.my_role !== 'admin') return;
        setEwSaving(true);
        try {
            const res = await axios.put(
                `${API_BASE_URL}/workspaces/${selectedWorkspace.id}/error-workflow`,
                { error_workflow_id: errorWorkflowId || null }
            );
            setErrorWorkflowId(res.data.error_workflow_id || '');
            setSelectedWorkspace(prev => prev ? { ...prev, error_workflow_id: res.data.error_workflow_id } : prev);
            setError(null);
        } catch (err) {
            setError("Failed to save error workflow: " + (err.response?.data?.detail || err.message));
        } finally {
            setEwSaving(false);
        }
    };

    const saveWorkspaceConcurrency = async () => {
        if (!selectedWorkspace || selectedWorkspace.my_role !== 'admin') return;
        const parsedLimit = Number.parseInt(workspaceConcurrencyLimit, 10);
        if (!Number.isFinite(parsedLimit) || parsedLimit < 1) {
            setError("Concurrency limit must be 1 or higher");
            return;
        }
        try {
            const res = await axios.put(
                `${API_BASE_URL}/workspaces/${selectedWorkspace.id}/concurrency`,
                { max_concurrent_runs: parsedLimit }
            );
            setWorkspaceConcurrencyLimit(res.data.max_concurrent_runs);
            setSelectedWorkspace(prev => prev ? { ...prev, max_concurrent_runs: res.data.max_concurrent_runs } : prev);
            setError(null);
        } catch (err) {
            setError("Failed to save concurrency limit");
        }
    };

    return (
        <div style={{ padding: '20px', color: 'var(--text-primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Workspaces</h2>
            </div>
            {error && <div style={{ color: 'var(--error-color)', marginBottom: '10px' }}>{error}</div>}
            
            <div style={{ display: 'flex', gap: '20px' }}>
                {/* List */}
                <div style={{ width: '300px', borderRight: '1px solid var(--border-color)', paddingRight: '20px' }}>
                    <div style={{ marginBottom: '10px', display: 'flex', gap: '5px' }}>
                        <input 
                            type="text" 
                            value={newWorkspaceName} 
                            onChange={e => setNewWorkspaceName(e.target.value)}
                            placeholder="New Workspace Name"
                            style={{ 
                                flex: 1, 
                                padding: '5px',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px'
                            }}
                        />
                        <button onClick={createWorkspace}>Create</button>
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {workspaces.map(ws => (
                            <li 
                                key={ws.id} 
                                onClick={() => selectWorkspace(ws)}
                                style={{ 
                                    padding: '10px', 
                                    cursor: 'pointer',
                                    background: selectedWorkspace?.id === ws.id ? 'var(--bg-secondary)' : 'transparent',
                                    borderRadius: '4px',
                                    marginBottom: '5px'
                                }}
                            >
                                {ws.name}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Details */}
                <div style={{ flex: 1 }}>
                    {selectedWorkspace ? (
                        <div>
                            <h3>{selectedWorkspace.name}</h3>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                                <button onClick={() => onSelectWorkspace(selectedWorkspace)}>Open Workspace</button>
                                {selectedWorkspace.my_role === 'admin' && (
                                    <button 
                                        onClick={deleteWorkspace}
                                        style={{ 
                                            background: 'transparent', 
                                            border: '1px solid var(--error-color)', 
                                            color: 'var(--error-color)',
                                            padding: '8px 16px',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Delete Workspace
                                    </button>
                                )}
                            </div>

                            <h4>AI Safety Policy</h4>
                            <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)' }}>
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px' }}>Allowed Providers (comma-separated)</label>
                                    <input
                                        type="text"
                                        value={policyProvidersInput}
                                        onChange={e => setPolicyProvidersInput(e.target.value)}
                                        disabled={selectedWorkspace.my_role !== 'admin'}
                                        placeholder="openai, gemini"
                                        style={{ width: '100%', padding: '6px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                                    />
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px' }}>Allowed Models (comma-separated)</label>
                                    <input
                                        type="text"
                                        value={policyModelsInput}
                                        onChange={e => setPolicyModelsInput(e.target.value)}
                                        disabled={selectedWorkspace.my_role !== 'admin'}
                                        placeholder="gpt-3.5-turbo, gemini-2.5-flash-lite"
                                        style={{ width: '100%', padding: '6px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                                    />
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(aiPolicy.redaction_enabled)}
                                        disabled={selectedWorkspace.my_role !== 'admin'}
                                        onChange={e => setAiPolicy(prev => ({ ...prev, redaction_enabled: e.target.checked }))}
                                    />
                                    Enable prompt/response redaction
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(aiPolicy.require_human_approval_for_high_impact)}
                                        disabled={selectedWorkspace.my_role !== 'admin'}
                                        onChange={e => setAiPolicy(prev => ({ ...prev, require_human_approval_for_high_impact: e.target.checked }))}
                                    />
                                    Require human approval for high-impact AI actions
                                </label>
                                {selectedWorkspace.my_role === 'admin' && (
                                    <button onClick={saveAiPolicy}>Save AI Policy</button>
                                )}
                            </div>

                            <h4>Scale & Reliability</h4>
                            <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)' }}>
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px' }}>Workspace Max Concurrent Runs</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={workspaceConcurrencyLimit}
                                        onChange={e => setWorkspaceConcurrencyLimit(e.target.value)}
                                        disabled={selectedWorkspace.my_role !== 'admin'}
                                        style={{ width: '100%', padding: '6px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                                    />
                                </div>
                                {selectedWorkspace.my_role === 'admin' && (
                                    <button onClick={saveWorkspaceConcurrency}>Save Concurrency</button>
                                )}
                            </div>

                            <h4>Error Workflow</h4>
                            <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)' }}>
                                <p style={{ margin: '0 0 10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                    Default workflow to trigger automatically when any workflow in this workspace reaches a terminal <strong>failed</strong> state. Individual workflows can override this setting.
                                </p>
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px' }}>Default Error Workflow</label>
                                    <SearchableSelect
                                        value={errorWorkflowId}
                                        onChange={setErrorWorkflowId}
                                        options={[
                                            { value: '', label: '— None —' },
                                            ...wsWorkflows.map(w => ({ value: w.id, label: w.name })),
                                        ]}
                                        placeholder="— None —"
                                        disabled={selectedWorkspace.my_role !== 'admin'}
                                        style={{ width: '100%', padding: '6px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}
                                    />
                                </div>
                                {selectedWorkspace.my_role === 'admin' && (
                                    <button onClick={saveWorkspaceErrorWorkflow} disabled={ewSaving}>
                                        {ewSaving ? 'Saving…' : 'Save Error Workflow'}
                                    </button>
                                )}
                            </div>

                            {selectedWorkspace.my_role === 'admin' && (
                                <>
                                    <h4>Pending AI Approvals</h4>
                                    {approvals.length === 0 ? (
                                        <div style={{ marginBottom: '20px', color: 'var(--text-tertiary)' }}>No pending approvals.</div>
                                    ) : (
                                        <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {approvals.map(approval => (
                                                <div key={approval.id} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px', background: 'var(--bg-secondary)' }}>
                                                    <div style={{ marginBottom: '6px' }}>
                                                        <strong>{approval.action}</strong> · {approval.provider} · {approval.model}
                                                    </div>
                                                    <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                                                        Request ID: {approval.id}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button onClick={() => reviewApproval(approval.id, 'approve')}>Approve</button>
                                                        <button onClick={() => reviewApproval(approval.id, 'reject')} style={{ color: 'var(--error-color)' }}>Reject</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            
                            <h4>Members</h4>
                            {selectedWorkspace.my_role === 'admin' && (
                                <div style={{ marginBottom: '10px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <input 
                                        type="text" 
                                        value={newMemberUsername} 
                                        onChange={e => setNewMemberUsername(e.target.value)}
                                        placeholder="Username"
                                        style={{ 
                                            padding: '5px', 
                                            width: '150px',
                                            background: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    <select 
                                        value={newMemberRole} 
                                        onChange={e => setNewMemberRole(e.target.value)}
                                        style={{ 
                                            padding: '5px',
                                            background: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <option value="viewer">Viewer</option>
                                        <option value="editor">Editor</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                    <button onClick={addMember}>Add Member</button>
                                </div>
                            )}

                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                                        <th style={{ padding: '8px' }}>User</th>
                                        <th style={{ padding: '8px' }}>Role</th>
                                        <th style={{ padding: '8px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map(m => (
                                        <tr key={m.user_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ padding: '8px' }}>{m.username} (ID: {m.user_id})</td>
                                            <td style={{ padding: '8px' }}>
                                                <select 
                                                    value={m.role}
                                                    onChange={(e) => updateMemberRole(m.user_id, e.target.value)}
                                                    disabled={selectedWorkspace.my_role !== 'admin'}
                                                    style={{ 
                                                        background: 'var(--bg-secondary)', 
                                                        color: 'var(--text-primary)', 
                                                        border: '1px solid var(--border-color)', 
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: selectedWorkspace.my_role !== 'admin' ? 'default' : 'pointer',
                                                        opacity: selectedWorkspace.my_role !== 'admin' ? 0.7 : 1
                                                    }}
                                                >
                                                     <option value="viewer">Viewer</option>
                                                     <option value="editor">Editor</option>
                                                     <option value="admin">Admin</option>
                                                </select>
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                {selectedWorkspace.my_role === 'admin' && (
                                                    <button onClick={() => removeMember(m.user_id)} style={{ color: 'var(--error-color)' }}>Remove</button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div>Select a workspace to manage</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WorkspaceManager;
